import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Eye,
  Trash2,
  Loader2,
  FileText,
  User,
  Calendar,
  TrendingUp,
  Edit,
  Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmailCampaignsDashboard() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [campaignsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/email-campaigns`),
        axios.get(`${API}/email-campaigns/profiles`)
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setProfiles(profilesRes.data.profiles || []);
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/email-campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleGetStarted = () => {
    if (profiles.length === 0) {
      navigate('/email-campaigns/profile');
    } else {
      navigate('/email-campaigns/create');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Email Sequences</h1>
          <p className="text-white/60">
            Direct response email campaigns using proven frameworks
          </p>
        </div>
        <Button
          onClick={handleGetStarted}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Sequence
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Total Campaigns</p>
                <p className="text-3xl font-bold text-white">{campaigns.length}</p>
              </div>
              <div className="p-3 bg-[#00d4aa]/20 rounded-lg">
                <Mail className="w-6 h-6 text-[#00d4aa]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Business Profiles</p>
                <p className="text-3xl font-bold text-white">{profiles.length}</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <User className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Emails Generated</p>
                <p className="text-3xl font-bold text-white">
                  {campaigns.reduce((sum, c) => sum + (c.emails?.length || 0), 0)}
                </p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Profiles Section */}
      {profiles.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Business Profiles</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/email-campaigns/profile')}
            >
              <Plus className="w-4 h-4 mr-1" />
              New Profile
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.map((profile, idx) => (
              <Card key={idx} className="card hover:border-white/20 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Briefcase className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold mb-1">
                        {profile.business_name}
                      </h3>
                      <div className="text-sm text-white/60 space-y-1">
                        <p>{profile.industry}</p>
                        <p>Tone: {profile.tone_preference}</p>
                        <p>From: {profile.sender_name}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/email-campaigns/profile/${profile.profile_id}`)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card className="card">
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No Email Campaigns Yet
            </h3>
            <p className="text-white/60 mb-6">
              {profiles.length === 0
                ? 'Start by creating a business profile, then generate your first email sequence'
                : 'Create your first direct response email sequence'}
            </p>
            <Button
              onClick={handleGetStarted}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              {profiles.length === 0 ? 'Create Business Profile' : 'Create Sequence'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white mb-4">Your Campaigns</h2>
          {campaigns.map((campaign, idx) => (
            <Card key={idx} className="card hover:border-white/20 transition-all">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Left: Info */}
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-[#00d4aa]/20 rounded-lg">
                      <Mail className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {campaign.offer_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {campaign.business_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {campaign.emails?.length || 0} emails
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          {campaign.awareness_level}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/email-campaigns/${campaign.campaign_id}`)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteCampaign(campaign.campaign_id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailCampaignsDashboard;
